import { useMemo, useState, type ChangeEvent } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import type { GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { parseISO } from "date-fns";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "layouts/pages/account/components/FormField";
import { LojaDataGrid, LojaPageContainer } from "layouts/pages/loja/common";
import { useLogsCartao } from "../dashboard/hooks/useLogsCartao";
import { formatCurrency } from "helpers/utils";
import { formatDate } from "helpers/formatDate";
import type {
  ILogCartaoEntry,
  LogCobrancaCategoria,
  LogCobrancaStatus,
} from "interfaces/logsCartao";
import { CATEGORIA_LABELS, STATUS_FILTER_OPTIONS, STATUS_LABELS } from "interfaces/logsCartao";
import LogCobrancaDetailDialog from "./components/LogCobrancaDetailDialog";
import { statusToggleSx } from "./logs-cobrancas.theme";

const ACCENT = "#F96030";

type ViewMode = "tabela" | "raw";

function formatLogCartaoValor(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const reais = Number.isInteger(value) ? value / 100 : value;
  return formatCurrency(reais);
}

function formatLogTimestamp(iso: string): string {
  if (!iso) return "—";
  try {
    return formatDate(parseISO(iso), "dd/MM/yyyy HH:mm:ss");
  } catch {
    return iso;
  }
}

function StatusPill({ status }: { status: LogCobrancaStatus }) {
  const label = STATUS_LABELS[status] ?? status;
  const styles: Record<
    LogCobrancaStatus,
    { bg: string; border: string; color: string }
  > = {
    SUCESSO: {
      bg: "rgba(46, 125, 50, 0.1)",
      border: "#2e7d32",
      color: "#2e7d32",
    },
    ERRO: {
      bg: "rgba(211, 47, 47, 0.1)",
      border: "#d32f2f",
      color: "#d32f2f",
    },
    AVISO: {
      bg: "rgba(237, 108, 2, 0.1)",
      border: "#ed6c02",
      color: "#ed6c02",
    },
    INFO: {
      bg: "rgba(249, 96, 48, 0.1)",
      border: ACCENT,
      color: ACCENT,
    },
  };
  const s = styles[status] ?? styles.INFO;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: s.border,
        bgcolor: s.bg,
        color: s.color,
        fontSize: "0.8125rem",
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

function levelChipColor(level: string): "default" | "info" | "error" {
  if (level === "error") return "error";
  if (level === "info") return "info";
  return "default";
}

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
        flex: "1 1 140px",
      }}
    >
      <Typography variant="h4" fontWeight="bold" color={accent ? ACCENT : "text.primary"}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LogsCobrancasPage() {
  const [viewMode, setViewMode] = useState<ViewMode>("tabela");
  const [selectedLog, setSelectedLog] = useState<ILogCartaoEntry | null>(null);
  const {
    rows,
    raw,
    resumo,
    loading,
    error,
    filters,
    setFilter,
    clearFilters,
    isFiltered,
    isEmpty,
    page,
    pageSize,
    total,
    refetch,
    onPaginationModelChange,
  } = useLogsCartao();

  const columns = useMemo<GridColDef<ILogCartaoEntry>[]>(
    () => [
      {
        field: "timestamp",
        headerName: "Data/Hora",
        width: 168,
        valueFormatter: (params) =>
          formatLogTimestamp(String(params.value ?? "")),
      },
      {
        field: "level",
        headerName: "Nível",
        width: 88,
        renderCell: (params: GridRenderCellParams<ILogCartaoEntry>) => (
          <Chip
            size="small"
            label={String(params.value ?? "").toUpperCase()}
            color={levelChipColor(String(params.value ?? ""))}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.65rem" }}
          />
        ),
      },
      {
        field: "categoria",
        headerName: "Tipo",
        width: 150,
        valueFormatter: (params) =>
          CATEGORIA_LABELS[params.value as LogCobrancaCategoria] ??
          String(params.value ?? "—"),
      },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        renderCell: (params: GridRenderCellParams<ILogCartaoEntry>) => (
          <StatusPill status={params.value as LogCobrancaStatus} />
        ),
      },
      {
        field: "numeroInscricao",
        headerName: "Referência",
        flex: 1,
        minWidth: 180,
        valueFormatter: (params) => String(params.value ?? "—"),
      },
      {
        field: "totalInscricao",
        headerName: "Valor",
        width: 110,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => {
          const value = params.value;
          if (value == null) return "—";
          return formatLogCartaoValor(
            typeof value === "number" ? value : Number(value)
          );
        },
      },
      {
        field: "installments",
        headerName: "Parc.",
        width: 64,
        align: "center",
        headerAlign: "center",
        valueFormatter: (params) =>
          params.value != null ? String(params.value) : "—",
      },
      {
        field: "customerName",
        headerName: "Cliente",
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => params.row.customer?.name ?? "—",
      },
      {
        field: "message",
        headerName: "Mensagem",
        flex: 1.5,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<ILogCartaoEntry>) => {
          const detail = params.row.detail;
          const msg = String(params.value ?? "—");
          if (!detail) return msg;
          return (
            <Tooltip title={detail} arrow placement="top">
              <Typography variant="caption" sx={{ cursor: "help" }}>
                {msg}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "erroCodigo",
        headerName: "Cód.",
        width: 72,
        valueFormatter: (params) => String(params.value ?? "—"),
      },
    ],
    []
  );

  const handlePagination = (model: GridPaginationModel) => {
    onPaginationModelChange(model);
  };

  return (
    <LojaPageContainer
      title="Logs de Cobranças"
      subtitle="Monitoramento de cobranças de inscrições e pedidos da loja — tentativas, erros, PIX, boletos e notificações (últimos 200 eventos)."
      actions={
        <MDButton
          variant="outlined"
          color="dark"
          size="small"
          startIcon={<RefreshOutlinedIcon />}
          disabled={loading}
          onClick={refetch}
        >
          {loading ? "Atualizando…" : "Atualizar"}
        </MDButton>
      }
    >
      <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
        <StatMini label="Total no período" value={resumo.total} accent />
        <StatMini label="Tentativas cartão" value={resumo.cartaoTentativas} />
        <StatMini label="Erros cartão" value={resumo.cartaoErros} />
        <StatMini label="Notificações" value={resumo.notificacoes} />
        <StatMini label="Boletos gerados" value={resumo.boletos} />
        <StatMini label="PIX gerados" value={resumo.pixGerados ?? 0} />
      </Stack>

      <Box
        mb={2}
        p={2}
        sx={{
          borderRadius: 2,
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          borderLeft: `4px solid ${ACCENT}`,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-end" }}
          >
            <FormField
              label="Referência (EVT / PED)"
              placeholder="EVT-... ou PED-..."
              value={filters.numeroInscricao}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFilter("numeroInscricao", e.target.value)
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: "background.paper" }}
            />
            <FormField
              label="Nome do cliente"
              placeholder="Buscar por nome"
              value={filters.nomeCliente}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFilter("nomeCliente", e.target.value)
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: "background.paper" }}
            />
            <FormField
              label="CPF"
              placeholder="000.000.000-00"
              value={filters.cpf}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFilter("cpf", e.target.value)
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: "background.paper" }}
            />
          </Stack>

          <FormField
            select
            label="Tipo de evento"
            value={filters.categoria}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFilter("categoria", e.target.value as LogCobrancaCategoria | "")
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: "background.paper", minWidth: 180 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {(Object.keys(CATEGORIA_LABELS) as LogCobrancaCategoria[]).map(
              (key) => (
                <MenuItem key={key} value={key}>
                  {CATEGORIA_LABELS[key]}
                </MenuItem>
              )
            )}
          </FormField>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "flex-end" }}
            justifyContent="space-between"
          >
            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Status
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={filters.status}
                onChange={(_, next: LogCobrancaStatus | "" | null) => {
                  if (next === null) return;
                  setFilter("status", next);
                }}
                sx={statusToggleSx}
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <ToggleButton key={opt.value || "all-status"} value={opt.value}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              {isFiltered ? (
                <MDButton variant="text" color="dark" size="small" onClick={clearFilters}>
                  Limpar
                </MDButton>
              ) : null}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={(_, next: ViewMode | null) => {
                  if (next) setViewMode(next);
                }}
                sx={{
                  "& .MuiToggleButton-root": {
                    px: 1.5,
                    textTransform: "none",
                    gap: 0.5,
                  },
                }}
              >
                <ToggleButton value="tabela">
                  <TableChartOutlinedIcon sx={{ fontSize: "1rem !important" }} />
                  Tabela
                </ToggleButton>
                <ToggleButton value="raw">
                  <CodeOutlinedIcon sx={{ fontSize: "1rem !important" }} />
                  Raw Log
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {error ? (
        <Box
          py={4}
          px={2}
          mb={2}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "error.light",
            borderRadius: 2,
            bgcolor: "rgba(211, 47, 47, 0.06)",
          }}
        >
          <MDTypography variant="body2" color="error" mb={1}>
            {error}
          </MDTypography>
          <MDButton variant="outlined" color="error" size="small" onClick={refetch}>
            Tentar novamente
          </MDButton>
        </Box>
      ) : null}

      {viewMode === "tabela" ? (
        isEmpty ? (
          <Box
            py={6}
            textAlign="center"
            sx={{
              border: "1px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
              bgcolor: "grey.50",
            }}
          >
            <CreditCardOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
            <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
              Nenhum log encontrado
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {isFiltered
                ? "Ajuste os filtros ou limpe a busca para ver mais registros."
                : "Ainda não há registros de cobrança no arquivo de logs."}
            </MDTypography>
          </Box>
        ) : (
          <LojaDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            paginationMode="client"
            rowCount={total}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={handlePagination}
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedLog(params.row)}
            autoHeight
            getRowClassName={(params) =>
              params.row.status === "ERRO" ? "logs-row-error" : ""
            }
            sx={{
              cursor: "pointer",
              "& .logs-row-error": {
                bgcolor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          />
        )
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            maxHeight: 520,
            overflow: "auto",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.300",
            bgcolor: "grey.900",
            color: "grey.100",
            fontSize: "0.75rem",
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {loading
            ? "Carregando…"
            : raw.trim() || "Nenhum conteúdo de log disponível."}
        </Box>
      )}

      {!loading && viewMode === "tabela" && total > 0 ? (
        <Stack direction="row" spacing={2} mt={1} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            Exibindo {total} registro(s) — últimos 200 eventos de pagamento.
          </Typography>
          {resumo.cartaoErros > 0 ? (
            <Chip
              size="small"
              icon={<ErrorOutlineIcon />}
              label={`${resumo.cartaoErros} erro(s) de cartão`}
              color="error"
              variant="outlined"
            />
          ) : null}
          {resumo.notificacoes > 0 ? (
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon />}
              label={`${resumo.notificacoes} notificação(ões)`}
              color="success"
              variant="outlined"
            />
          ) : null}
        </Stack>
      ) : null}

      <LogCobrancaDetailDialog
        open={selectedLog != null}
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </LojaPageContainer>
  );
}
